import { ControllerInterface } from "csi/action/Controller";
import comm from "csi/comm";
import locale from "csi/locale";
import util from "csi/util";
import xml from "csi/xml";
import declare from "dojo/_base/declare";
import { MoveFromInterface } from "./MoveFromController";

const MultiActionPopupMixin = declare(null, {
	/** @property {Number} nStatusLoadingMinDelay - minimum time the loading popup needs to remain open in milliseconds */
	nStatusLoadingMinDelay: 1500,

	/** @property {String} numCreateField - name of field containing number of nodes to create */
	numCreateField: "",

	/** @property {String} loadingMessageTemplateSingular - localization template string for loading message when creating 1 node */
	loadingMessageTemplateSingular: "",
	
	/** @property {String} loadingMessageTemplatePlural - localization template string for loading message when creating multiple nodes */
	loadingMessageTemplatePlural: "",

	/** @property {String} successMessageTemplateSingular - localization template string for success message when creating 1 node */
	successMessageTemplateSingular: "",
	
	/** @property {String} successMessageTemplatePlural - localization template string for success message when creating multiple nodes */
	successMessageTemplatePlural: "",

	/** @property {ControllerInterface} _controller - dummy interface to expose controller variables */
	_controller: null,

	/** @property {Number} _nStatusLoadingStart - time when loading popup was created in milliseconds */
	_nStatusLoadingStart: null,

	/** @property {Number} _nNumCreate - number of nodes to create */
	_nNumCreate: null,

	/** @property {String} _statusLoadingMessage - content of loading popup */
	_statusLoadingMessage: "",

	/** @property {Boolean} _validForm - determines if the specified form data exists and is valid */
	_validForm: false,

	
	constructor() {
		this._controller = this as unknown as ControllerInterface;
	},

	/**
	 * Wait until loading popup has been open for the minimum amount of time
	 */
	async waitForLoadPopup() {
		const loadingTime = Date.now() - this._nStatusLoadingStart;
		const remainingTime = Math.max(this.nStatusLoadingMinDelay - loadingTime, 0);

		await util.wait(remainingTime);
	},

	/**
	 * @override
	 * Display progress dialog when action is performed
	 */
	waitForPublish() {
		if (this._validForm) {
			this._controller.hidePopup();

			this._controller.displayProgressDialog({
				closable: false,
				message: this._statusLoadingMessage,
			});

			this._nStatusLoadingStart = Date.now();
		}
		
		this.inherited(arguments);
	},

	/**
	 * @override
	 */
	postProcessSuccess() {
		this.inherited(arguments);
		const isSaveAndNewAction = this._controller.m_proxy?.m_action.m_bRepeat;

		if (isSaveAndNewAction) {
			this._controller.showPopup();
		}
	},

	/**
	 * @override
	 * Set popup titles and messages
	 */
	publish() {
		this._validForm = true;
		const formData = this._controller.m_formData;
		this._nNumCreate = parseInt(formData.getFieldValue(this.numCreateField, true));

		// Check that the field value is valid
		if (!this._nNumCreate) {
			this._validForm = false;
		}
		else {
			const templateString = this._nNumCreate == 1 ? this.loadingMessageTemplateSingular : this.loadingMessageTemplatePlural;
			this._statusLoadingMessage = locale.resource(templateString, [this._nNumCreate]);
		}

		this.inherited(arguments);
	},

	/**
	 * @override
	 * Format string to display 
	 */
	getSuccess() {
		if (!this._validForm) {
			return this.inherited(arguments);
		}

		const templateString = this._nNumCreate == 1 ? this.successMessageTemplateSingular : this.successMessageTemplatePlural;
		return locale.resource(templateString, [this._nNumCreate]);
	},

	/**
	 * @override
	 * If an error occurs during the action, restore the dojo form
	 */
	async _onPublishError(err: Error) {
		if (this._validForm) {
			await this.waitForLoadPopup();
			this._controller.showPopup();
		}

		this._controller.publishError(err);
	},

	/**
	 * @override
	 * Wait for loading popup if it hasn't been displayed for long enough
	 */
	async _onPublishResponse(response: Response) {
		if (this._validForm) {
			await this.waitForLoadPopup();
		}

		this._controller.publishResponse(response);
	},

	/**
	 * @override
	 * Control multicopy behavior for Food and FoodSKU
	 */
	_writeRequestParameters(call) {
		const controller = this as unknown as MoveFromInterface;

		const numCreate = parseInt(this._controller.m_formData.getFieldValue(this.numCreateField, true));
		const numURLs = isNaN(numCreate) ? 1 : numCreate;

		const urls = comm.createUrls(numURLs);
		call.addParameter("URLs", xml.ATTR_REFLIST, urls);
		controller.m_value = urls;
		
		this.inherited(arguments);
	},
});

export = MultiActionPopupMixin;